import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Logo from "../../images/TestAssessify.png";
import Loader from "../../components/loader/Loader";
import { exams } from "../../api/exams";

// SSH assessment links land here before entering the existing instructions flow.
const OnboardExam = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [message, setMessage] = useState("");
  const onboardingStarted = useRef(false);
  const assessmentCompleted = /already completed/i.test(message);

  const returnToSsh = () => {
    if (window.parent !== window) {
      window.parent.postMessage(
        {
          type: "ssh-assessment-completed",
          assessmentId: searchParams.get("assessment_id"),
        },
        "*"
      );
      return;
    }
    window.close();
    window.setTimeout(() => window.history.back(), 100);
  };

  useEffect(() => {
    if (onboardingStarted.current) return;
    onboardingStarted.current = true;

    const onboard = async () => {
      try {
        const name = searchParams.get("name") || "";
        const email = searchParams.get("email") || "";
        const assessmentId = searchParams.get("assessment_id");
        if (!assessmentId) throw new Error("This assessment link is missing an assessment id.");

        const cohortParticipantId = searchParams.get("cohort_participant_id") || "";
        const journeyCohortId = searchParams.get("journey_cohort_id") || "";
        const saveScoreUrl = searchParams.get("save_score_url") || "";
        const source = searchParams.get("source") || "ssh";
        const response = await exams.autoOnboardUser({
          name,
          email,
          assessment_id: assessmentId,
          source,
          meta: {
            cohort_participant_id: cohortParticipantId,
            journey_cohort_id: journeyCohortId,
            save_score_url: saveScoreUrl,
          },
        });
        const assessment = Array.isArray(response?.data) ? response.data[0] : null;
        if (!assessment) throw new Error(response?.message || "Unable to start this assessment.");
        const token =
          response?.token ||
          response?.data?.token ||
          assessment?.token;

        if (!token) throw new Error("The assessment session did not include an authorization token.");

        sessionStorage.setItem("userToken", token);
        if (response?.user) {
          sessionStorage.setItem("user", JSON.stringify(response.user));
        }
        sessionStorage.setItem("ssh_app_key", searchParams.get("app_key") || "");
        sessionStorage.setItem("ssh_source", source);
        sessionStorage.setItem("ssh_cohort_participant_id", cohortParticipantId);
        sessionStorage.setItem("ssh_journey_cohort_id", journeyCohortId);
        sessionStorage.setItem("ssh_save_score_url", saveScoreUrl);
        sessionStorage.setItem("active-assessment-id", assessment.assessment_id);
        sessionStorage.setItem("active-exam-id", assessment.exam_id);
        sessionStorage.setItem("active-assessment-user-id", assessment.user_exam_id);
        sessionStorage.setItem("active-assessment", JSON.stringify(assessment));
        const userId =
          response?.user?.user_id ||
          response?.user?.id ||
          response?.user_id ||
          assessment?.user_id;
        if (userId) sessionStorage.setItem("user_id", userId);
        sessionStorage.setItem("attempt_count", assessment.number_of_attempts || 0);
        sessionStorage.setItem("time_left", assessment.time_left || assessment.exam_duration);
        sessionStorage.setItem("duration", assessment.exam_duration);
        sessionStorage.setItem("ql", assessment.number_of_questions);
        sessionStorage.setItem("starting-index", assessment.number_of_questions_answered || 0);
        navigate("/candidate/assessments/instructions", { replace: true });
      } catch (error) {
        setMessage(
          error?.response?.data?.message ||
          error?.message ||
          "Unable to start this assessment."
        );
      }
    };
    onboard();
  }, [navigate, searchParams]);

  return (
    <div className="bg-gray-50 min-h-screen px-4 py-10 flex items-center justify-center">
      <div className="w-full max-w-lg rounded-2xl border border-gray-200 bg-white px-6 py-10 text-center shadow-lg md:px-12">
        <img src={Logo} alt="TestAssessify" className="h-14 w-auto mx-auto mb-10" />
        {!message ? (
          <div className="flex flex-col items-center gap-5">
            <Loader />
            <p className="text-gray-600">Preparing your assessment…</p>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <div className={`mb-5 flex h-14 w-14 items-center justify-center rounded-full text-2xl ${assessmentCompleted ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
              {assessmentCompleted ? "✓" : "!"}
            </div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {assessmentCompleted ? "Assessment completed" : "Unable to start assessment"}
            </h1>
            <p className="mt-3 max-w-sm text-base leading-6 text-gray-600">{message}</p>
            {assessmentCompleted && (
              <button type="button" onClick={returnToSsh} className="mt-8 rounded-md bg-primary px-6 py-3 text-sm font-medium text-white">
                Return to Service School House
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardExam;
